import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, Plus, Activity, Search, Trash2 } from 'lucide-react';
import ModalEditarProducto from '../componentes/ModalEditarProducto';
import ImageModal from '../componentes/ImageModal';
import { obtenerProductos, agregarProducto, registrarMovimiento, eliminarProducto, actualizarProducto, registrarMovimientoStock, obtenerUrlArchivo } from '../servicios/api';

const formatPurchasePrice = (price) => {
  if (price === null || price === undefined || price === '') return 'Sin coste';
  const numericPrice = Number(price);
  return Number.isFinite(numericPrice)
    ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(numericPrice)
    : 'Sin coste';
};

const InventorySystem = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await obtenerProductos();
        if (response.exito) {
          setProducts(response.productos);
        } else {
          setError(response.mensaje);
        }
      } catch (err) {
        setError('Error al conectar con la API de productos.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showStockMovement, setShowStockMovement] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const [newProduct, setNewProduct] = useState({
    name: '', modelo: '', marca: '', category: '', stock: 0, minStock: 0, price: 0, purchasePrice: '', location: '', image: null
  });

  const [stockMovement, setStockMovement] = useState({
    productId: '', type: 'Entrada', quantity: 0, reason: '', user: 'Usuario Actual'
  });

  const categories = ['all', ...new Set(products.map(p => p.categoria))];
  const lowStockProducts = products.filter(p => p.stock_actual <= p.stock_minimo);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.modelo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.categoria === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('nombre', newProduct.name);
      formData.append('modelo', newProduct.modelo);
      formData.append('marca', newProduct.marca);
      formData.append('categoria', newProduct.category);
      formData.append('stock_actual', parseInt(newProduct.stock));
      formData.append('stock_minimo', parseInt(newProduct.minStock));
      formData.append('precio', parseFloat(newProduct.price));
      if (newProduct.purchasePrice !== '') formData.append('precio_compra', parseFloat(newProduct.purchasePrice));
      formData.append('ubicacion', newProduct.location);
      if (newProduct.image) {
        formData.append('imagen_producto', newProduct.image);
      }

      const response = await agregarProducto(formData);
      if (response.exito) {
        alert(response.mensaje);
        // Recargar productos después de agregar uno nuevo
        const updatedProductsResponse = await obtenerProductos();
        if (updatedProductsResponse.exito) {
          setProducts(updatedProductsResponse.productos);
        } else {
          setError(updatedProductsResponse.mensaje);
        }
        setNewProduct({ name: '', modelo: '', marca: '', category: '', stock: 0, minStock: 0, price: 0, purchasePrice: '', location: '' });
        setShowAddProduct(false);
      } else {
        setError(response.mensaje);
      }
    } catch (err) {
      setError('Error al conectar con la API para agregar producto.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStockMovement = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const product = products.find(p => p.id_producto === stockMovement.productId);
    if (!product) {
      setError("Producto no encontrado.");
      setLoading(false);
      return;
    }

    // Asumiendo que el id_usuario se obtiene de la sesión o del token
    // Por ahora, usaremos un valor de ejemplo. En una app real, lo obtendrías del contexto de autenticación.
    const id_usuario_actual = 1; // ¡CAMBIAR ESTO POR EL ID REAL DEL USUARIO AUTENTICADO!

    try {
      const movementData = {
        id_producto: product.id_producto,
        tipo_movimiento: stockMovement.type,
        cantidad: parseInt(stockMovement.quantity),
        motivo: stockMovement.reason,
        id_usuario: id_usuario_actual,
      };

      const response = await registrarMovimientoStock(movementData);

      if (response.exito) {
        alert(response.mensaje);
        // Recargar productos y movimientos después de un movimiento exitoso
        const updatedProductsResponse = await obtenerProductos();
        if (updatedProductsResponse.exito) {
          setProducts(updatedProductsResponse.productos);
        } else {
          setError(updatedProductsResponse.mensaje);
        }
        // Aquí podrías tener una API para obtener movimientos si los quieres mostrar
        // Por ahora, los movimientos no se cargan dinámicamente, solo se registran.
        setStockMovement({ productId: '', type: 'Entrada', quantity: 0, reason: '', user: 'Usuario Actual' });
        setShowStockMovement(false);
      } else {
        setError(response.mensaje);
      }
    } catch (err) {
      setError('Error al conectar con la API para registrar movimiento.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar "${product.nombre}" del inventario?`)) {
      setDeletingProductId(String(product.id_producto));
      try {
        const response = await eliminarProducto(product.id_producto);
        if (response.exito) {
          setProducts((currentProducts) =>
            currentProducts.filter((item) => String(item.id_producto) !== String(product.id_producto))
          );
        } else {
          window.alert(response.mensaje || 'No se pudo eliminar el producto.');
        }
      } catch (err) {
        window.alert(err.response?.data?.mensaje || 'Error al eliminar el producto.');
        console.error(err);
      } finally {
        setDeletingProductId(null);
      }
    }
  };

  const handleEditProduct = (product) => {
    console.log('handleEditProduct called with product:', product);
    setProductToEdit(product);
    setShowEditProductModal(true);
    console.log('showEditProductModal:', true, 'productToEdit:', product);
  };

  const handleSaveEditedProduct = async (editedProductData) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      // Append all fields from editedProductData
      for (const key in editedProductData) {
        // Skip id_producto if it's not needed in the form data for update,
        // but our backend expects it.
        // Also, handle image_url separately if it's not a File object
        if (key === 'imagen_url' && typeof editedProductData[key] === 'string') {
            formData.append('imagen_url_existente', editedProductData[key]);
        } else if (editedProductData[key] !== null && editedProductData[key] !== undefined) {
            formData.append(key, editedProductData[key]);
        }
      }
      // Ensure id_producto is always sent
      formData.append('id_producto', editedProductData.id_producto);


      const response = await actualizarProducto(formData);
      if (response.exito) {
        alert(response.mensaje);
        // Recargar productos después de actualizar uno
        const updatedProductsResponse = await obtenerProductos();
        if (updatedProductsResponse.exito) {
          setProducts(updatedProductsResponse.productos);
        } else {
          setError(updatedProductsResponse.mensaje);
        }
        setShowEditProductModal(false); // Close the modal
        setProductToEdit(null); // Clear the product being edited
      } else {
        setError(response.mensaje);
      }
    } catch (err) {
      setError('Error al actualizar el producto.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const menuItems = [
    { id: 'current', label: 'Inventario Actual', icon: Package },
    { id: 'lowstock', label: 'Productos de Bajo Stock', icon: AlertTriangle },
    { id: 'add', label: 'Agregar Producto', icon: Plus },
    { id: 'movements', label: 'Movimiento de Stock', icon: Activity }
  ];

  const renderCurrentInventory = () => {
    console.log('Rendering Current Inventory. Filtered Products:', filteredProducts);
    return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-100">Inventario Actual</h2>
        <div className="flex flex-col w-full sm:flex-row sm:gap-2 sm:w-auto">
          <div className="relative w-full mb-2 sm:mb-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar productos..."
              className="pl-10 pr-4 py-2 border border-gray-700 rounded-lg w-full bg-gray-900 text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 border border-gray-700 rounded-lg bg-gray-900 text-gray-300 w-full"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'Todas las categorías' : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Coste compra</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Imagen</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Modelo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Marca</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Categoría</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Stock</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Stock Mín.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Precio venta</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Ubicación</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Estado</th>
                <th className="sticky right-0 z-10 bg-gray-900 px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.8)]">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {filteredProducts.map((product) => (
                <tr key={product.id_producto} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-100">{product.nombre}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {formatPurchasePrice(product.precio_compra)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.imagen_url && (
                      <div className="cursor-pointer" onClick={() => handleImageClick(obtenerUrlArchivo(product.imagen_url))}>
                        <img src={obtenerUrlArchivo(product.imagen_url)} alt={product.nombre} className="h-10 w-10 object-cover" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{product.modelo}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{product.marca}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{product.categoria}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${product.stock_actual <= product.stock_minimo ? 'text-red-400' : 'text-gray-100'}`}>
                      {product.stock_actual}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{product.stock_minimo}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-100">€{product.precio}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{product.ubicacion}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {Number(product.stock_actual) <= Number(product.stock_minimo) ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Bajo Stock
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-300">
                        Normal
                      </span>
                    )}
                  </td>
                  <td className="sticky right-0 bg-gray-800 px-4 py-4 whitespace-nowrap text-right text-sm font-medium shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.8)]">
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="rounded px-2 py-1 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200 mr-1"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product)}
                      disabled={deletingProductId === String(product.id_producto)}
                      aria-label={`Eliminar ${product.nombre}`}
                      className="inline-flex min-w-24 items-center justify-center gap-1 rounded bg-red-600 px-3 py-1.5 text-white hover:bg-red-500 disabled:cursor-wait disabled:opacity-60"
                    >
                      <Trash2 className="h-4 w-4" />
                      {deletingProductId === String(product.id_producto) ? 'Eliminando…' : 'Eliminar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  }

  const renderLowStock = () => {
    console.log('Rendering Low Stock. Low Stock Products:', lowStockProducts);
    return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-100">Productos de Bajo Stock</h2>
        <div className="flex items-center space-x-2 text-sm text-gray-400">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span>{lowStockProducts.length} productos requieren atención</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {lowStockProducts.map((product) => (
          <div key={product.id_producto} className="bg-gray-800 rounded-lg shadow border-l-4 border-red-400 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-100 mb-2">{product.nombre}</h3>
                <p className="text-sm text-gray-400 mb-3">{product.modelo} • {product.categoria}</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Stock actual:</span>
                    <span className="font-medium text-red-400">{product.stock_actual}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Stock mínimo:</span>
                    <span className="font-medium text-gray-100">{product.stock_minimo}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Para llegar al mínimo:</span>
                    <span className="font-medium text-red-400">{Math.max(0, Number(product.stock_minimo) - Number(product.stock_actual))}</span>
                  </div>
                </div>
              </div>
              <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
            </div>
            <div className="mt-4">
              <button 
                onClick={() => {
                  setStockMovement({...stockMovement, productId: product.id_producto.toString()});
                  setShowStockMovement(true);
                }}
                className="w-full px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-400 transition-colors"
              >
                Reabastecer
              </button>
            </div>
          </div>
        ))}
      </div>

      {lowStockProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-100 mb-2">¡Excelente!</h3>
          <p className="text-gray-400">Todos los productos tienen stock suficiente</p>
        </div>
      )}
    </div>
  );
  }

  const renderAddProduct = () => (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-100 mb-6">Agregar Nuevo Producto</h2>
      
      <div className="bg-gray-800 rounded-lg shadow p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nombre del Producto</label>
              <input
                type="text"
                required
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Modelo</label>
              <input
                type="text"
                required
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.modelo}
                onChange={(e) => setNewProduct({...newProduct, modelo: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Marca</label>
              <input
                type="text"
                required
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.marca}
                onChange={(e) => setNewProduct({...newProduct, marca: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Categoría</label>
              <input
                type="text"
                required
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.category}
                onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Ubicación</label>
              <input
                type="text"
                required
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.location}
                onChange={(e) => setNewProduct({...newProduct, location: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Stock Inicial</label>
              <input
                type="number"
                required
                min="0"
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.stock}
                onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Stock Mínimo</label>
              <input
                type="number"
                required
                min="0"
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.minStock}
                onChange={(e) => setNewProduct({...newProduct, minStock: e.target.value})}
              />
              <p className="mt-1 text-xs text-gray-500">Si el stock actual llega a este nivel o baja de él, el producto aparecerá en Bajo Stock.</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Precio de venta</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.price}
                onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Precio de compra</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                value={newProduct.purchasePrice}
                onChange={(e) => setNewProduct({...newProduct, purchasePrice: e.target.value})}
              />
              <p className="mt-1 text-xs text-gray-500">Se usará para calcular el coste de la pieza en los avisos.</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Imagen del Producto</label>
              <input
                type="file"
                className="w-full px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                onChange={(e) => setNewProduct({...newProduct, image: e.target.files[0]})}
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setNewProduct({ name: '', modelo: '', marca: '', category: '', stock: 0, minStock: 0, price: 0, purchasePrice: '', location: '', image: null })}
              className="px-6 py-2 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Limpiar
            </button>
            <button
              type="button"
              onClick={handleAddProduct} // Llama a la función handleAddProduct directamente
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-colors"
            >
              Agregar Producto
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMovements = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-100">Movimientos de Stock</h2>
        <button
          onClick={() => setShowStockMovement(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400 transition-colors flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Nuevo Movimiento</span>
        </button>
      </div>

      <div className="bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Cantidad</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Motivo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Usuario</th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {movements.map((movement) => (
                <tr key={movement.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-100">{movement.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100">{movement.productName}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      movement.type === 'Entrada' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {movement.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-100">{movement.quantity}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{movement.reason}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{movement.user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900">
      {loading && <div className="text-center text-gray-300 py-8">Cargando inventario...</div>}
      {error && <div className="text-center text-red-400 py-8">Error: {error}</div>}
      {!loading && !error && (
        <>
          {/* Header */}
          <div className="bg-gray-800 shadow">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-6">
                <div className="flex items-center space-x-3">
                  <Package className="w-8 h-8 text-blue-400" />
                  <h1 className="text-2xl font-bold text-gray-100">Sistema de Inventario</h1>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
            <nav className="flex space-x-1 mb-8 overflow-x-auto">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === item.id
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Content */}
            <div className="pb-8">
              {activeTab === 'current' && renderCurrentInventory()}
              {activeTab === 'lowstock' && renderLowStock()}
              {activeTab === 'add' && renderAddProduct()}
              {activeTab === 'movements' && renderMovements()}
            </div>
          </div>

          {/* Modal for Stock Movement */}
          {showStockMovement && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-semibold text-gray-100 mb-4">Registrar Movimiento de Stock</h3>
                
                <form onSubmit={handleStockMovement} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Producto</label>
                    <select
                      required
                      className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                      value={stockMovement.productId}
                      onChange={(e) => setStockMovement({...stockMovement, productId: e.target.value})}
                    >
                      <option value="">Seleccionar producto</option>
                      {products.map(product => (
                        <option key={product.id_producto} value={product.id_producto}>
                          {product.nombre} - Stock: {product.stock_actual}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Movimiento</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                      value={stockMovement.type}
                      onChange={(e) => setStockMovement({...stockMovement, type: e.target.value})}
                    >
                      <option value="Entrada">Entrada</option>
                      <option value="Salida">Salida</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Cantidad</label>
                    <input
                      type="number"
                      required
                      min="1"
                      className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                      value={stockMovement.quantity}
                      onChange={(e) => setStockMovement({...stockMovement, quantity: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Motivo</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-900 text-gray-300"
                      value={stockMovement.reason}
                      onChange={(e) => setStockMovement({...stockMovement, reason: e.target.value})}
                      placeholder="Ej: Venta, Compra, Devolución..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowStockMovement(false)}
                      className="px-4 py-2 text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-700"
                    >
                      Cancelar
                    </button>
                    <button
                  type="button"
                  onClick={handleStockMovement} // Llama a la función handleStockMovement directamente
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-400"
                >
                  Registrar
                </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Modal for Edit Product */}
          {console.log('Rendering ModalEditarProducto? showEditProductModal:', showEditProductModal, 'productToEdit:', productToEdit)}
          {showEditProductModal && productToEdit && (
            <ModalEditarProducto
              product={productToEdit}
              onClose={() => {
                setShowEditProductModal(false);
                setProductToEdit(null);
              }}
              onSave={handleSaveEditedProduct}
            />
          )}

          {/* Image Modal */}
          {showImageModal && selectedImage && (
            <ImageModal
              imageUrl={selectedImage}
              onClose={() => {
                setShowImageModal(false);
                setSelectedImage('');
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default InventorySystem;